// ============================================================================
// CHANGO V5.0 — Ops Archived Workspaces panel (admin-only)
// ============================================================================
// Task: #47 — ArchivedWorkspacesPanel + preview + two-person reactivate.
//
// Two things live here:
//
//   1. "归档预览"  — dry-run of the nightly soft-archive sweep. The tile
//      calls GET /api/ops/workspaces/archive-preview and lists each row
//      the sweep would flip. Optional threshold override inline so ops
//      can A/B tune workspace_archive_after_days before flipping it in
//      /ops/system-config.
//
//   2. "已归档工作区"  — readonly list filtered by ?status=archived. Each
//      row has a "申请重新激活" button that files a two-person proposal
//      (action_kind='workspace_reactivate') via
//        POST /api/ops/workspaces/:id/reactivate-propose  { reason }
//      Approving + executing happens in /ops/review-requests (unchanged) →
//      then the FINAL execute is POST /api/ops/workspaces/:id/reactivate-
//      from-archive with the approved review_request_id.
//
// All mutations go through the server-side auditOps() so ops_action_log
// has a paper trail; no client-side audit required.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';

const REASON_MIN = 8;
const REASON_MAX = 1000;
const ARCHIVE_AFTER_MIN = 1;
const ARCHIVE_AFTER_MAX = 365;

// ---- small presentational helpers ------------------------------------------

function Banner({ kind, children }) {
  if (!children) return null;
  const tone = {
    error: 'border-rose-500/40 bg-rose-900/30 text-rose-200',
    ok:    'border-emerald-500/40 bg-emerald-900/30 text-emerald-200',
    warn:  'border-amber-500/40 bg-amber-900/30 text-amber-200',
    info:  'border-slate-500/40 bg-slate-800/60 text-slate-200',
  }[kind || 'info'];
  return (
    <div className={`mb-4 rounded-md border px-4 py-2 text-sm ${tone}`}>
      {children}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('zh-CN', { hour12: false });
}

function daysAgo(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const d = (Date.now() - t) / 86_400_000;
  if (!Number.isFinite(d) || d < 0) return null;
  return d;
}

function StatusPill({ status }) {
  const tone = {
    archived:      'bg-slate-700 text-slate-200',
    frozen:        'bg-amber-900/60 text-amber-200',
    trial_expired: 'bg-rose-900/60 text-rose-200',
    active:        'bg-emerald-900/60 text-emerald-200',
    rejected:      'bg-slate-800 text-slate-400',
    pending_review:'bg-sky-900/60 text-sky-200',
  }[status] || 'bg-slate-800 text-slate-300';
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${tone}`}>
      {status || '—'}
    </span>
  );
}

// ---- archive-preview tile --------------------------------------------------

function ArchivePreviewTile() {
  const [override, setOverride] = useState(''); // empty string = use system default
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const overrideNum = override === '' ? null : Number.parseInt(override, 10);
  const overrideOk =
    override === '' ||
    (Number.isInteger(overrideNum) &&
      overrideNum >= ARCHIVE_AFTER_MIN &&
      overrideNum <= ARCHIVE_AFTER_MAX);

  const load = useCallback(async () => {
    if (!overrideOk) return;
    setLoading(true);
    setErr(null);
    try {
      const qs = override === '' ? '' : `?archive_after_days=${overrideNum}`;
      const res = await api.get(`/api/ops/workspaces/archive-preview${qs}`);
      setResult(res);
    } catch (e) {
      setErr(e instanceof HttpError ? `${e.code || 'ERROR'}: ${e.message}` : e.message);
    } finally {
      setLoading(false);
    }
  }, [override, overrideNum, overrideOk]);

  useEffect(() => {
    // First paint uses the server's current threshold.
    load();
    // Intentionally run once on mount; manual "刷新" covers subsequent loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const archiveRows = useMemo(
    () => (result?.items || []).filter((r) => r.action === 'archive'),
    [result]
  );
  const skipRows = useMemo(
    () => (result?.items || []).filter((r) => r.action !== 'archive'),
    [result]
  );

  return (
    <section className="mb-8 rounded-lg border border-slate-700 bg-slate-800/50 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-100">归档预览</h2>
          <p className="mt-1 text-xs text-slate-400">
            模拟每日软归档 sweep。当前阈值 {' '}
            <code className="rounded bg-slate-700 px-1">{result?.archive_after_days ?? '—'}</code>{' '}
            天，仅预览不修改任何数据。修改阈值请去{' '}
            <Link to="/ops/system-config" className="text-chango-300 hover:underline">
              /ops/system-config
            </Link>
            。
          </p>
        </div>
        <div className="flex items-end gap-2">
          <label className="block text-xs text-slate-400">
            临时覆盖阈值 (天)
            <input
              type="number"
              min={ARCHIVE_AFTER_MIN}
              max={ARCHIVE_AFTER_MAX}
              value={override}
              onChange={(e) => setOverride(e.target.value.trim())}
              placeholder="留空用系统默认"
              className="mt-1 block w-40 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-sm text-slate-100 focus:border-chango-500 focus:outline-none"
            />
          </label>
          <button
            onClick={load}
            disabled={!overrideOk || loading}
            className="mb-[2px] rounded-md bg-chango-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-chango-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? '加载中…' : '刷新预览'}
          </button>
        </div>
      </div>

      {!overrideOk && (
        <p className="mt-2 text-xs text-rose-300">
          阈值必须是 {ARCHIVE_AFTER_MIN} – {ARCHIVE_AFTER_MAX} 之间的整数
        </p>
      )}
      {err && <Banner kind="error">{err}</Banner>}

      {result && (
        <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
          <div className="rounded-md bg-slate-900/40 px-3 py-2">
            <div className="text-xs text-slate-400">将被归档</div>
            <div className="text-xl font-semibold text-rose-300 tabular-nums">
              {result.archive_count}
            </div>
          </div>
          <div className="rounded-md bg-slate-900/40 px-3 py-2">
            <div className="text-xs text-slate-400">仍在门槛内</div>
            <div className="text-xl font-semibold text-slate-300 tabular-nums">
              {result.skip_count}
            </div>
          </div>
          <div className="rounded-md bg-slate-900/40 px-3 py-2">
            <div className="text-xs text-slate-400">候选总数</div>
            <div className="text-xl font-semibold text-slate-300 tabular-nums">
              {result.total}
            </div>
          </div>
        </div>
      )}

      {result && archiveRows.length > 0 && (
        <details open className="mt-4">
          <summary className="cursor-pointer text-sm text-rose-300">
            将被归档的工作区 ({archiveRows.length})
          </summary>
          <ul className="mt-2 divide-y divide-slate-700 rounded-md border border-slate-700">
            {archiveRows.map((r) => (
              <li key={`arch-${r.wsId}`} className="flex items-center justify-between px-3 py-2 text-sm">
                <div>
                  <Link to={`/ops/workspaces/${r.wsId}`} className="font-mono text-chango-300 hover:underline">
                    #{r.wsId}
                  </Link>
                  {' · '}
                  <StatusPill status={r.registrationStatus} />
                </div>
                <div className="text-xs text-slate-400">
                  {Number.isFinite(r.daysInStatus)
                    ? `${r.daysInStatus.toFixed(1)} 天 · `
                    : ''}
                  {r.reason || ''}
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}

      {result && skipRows.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-slate-400">
            仍在门槛内 ({skipRows.length})
          </summary>
          <ul className="mt-2 divide-y divide-slate-700 rounded-md border border-slate-700">
            {skipRows.map((r) => (
              <li key={`skip-${r.wsId}`} className="flex items-center justify-between px-3 py-2 text-xs">
                <div>
                  <Link to={`/ops/workspaces/${r.wsId}`} className="font-mono text-chango-300 hover:underline">
                    #{r.wsId}
                  </Link>
                  {' · '}
                  <StatusPill status={r.registrationStatus} />
                </div>
                <div className="text-slate-400">
                  {Number.isFinite(r.daysInStatus)
                    ? `${r.daysInStatus.toFixed(1)} 天`
                    : '无时间戳'}
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}

      {result && result.total === 0 && (
        <p className="mt-3 text-sm text-slate-400">
          没有候选工作区 — 当前阈值下 sweep 不会动任何数据。
        </p>
      )}
    </section>
  );
}

// ---- reactivate proposal dialog --------------------------------------------

function ReactivateProposeDialog({ open, ws, onClose, onSubmitted }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setBusy(false);
      setErr(null);
    }
  }, [open, ws?.id]);

  if (!open || !ws) return null;

  const reasonTrim = reason.trim();
  const reasonOk = reasonTrim.length >= REASON_MIN && reasonTrim.length <= REASON_MAX;
  const formOk = reasonOk && !busy;

  const submit = async () => {
    if (!formOk) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.post(
        `/api/ops/workspaces/${ws.id}/reactivate-propose`,
        { reason: reasonTrim }
      );
      onSubmitted?.(res);
      onClose();
    } catch (e) {
      if (e instanceof HttpError && e.code === 'DUPLICATE_PROPOSAL') {
        setErr('该工作区已有待审批的重新激活提案 — 请去审批收件箱。');
      } else {
        setErr(e?.message || '提交失败');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/70"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg rounded-lg bg-slate-800 p-6 shadow-xl ring-1 ring-slate-700">
        <h3 className="text-base font-semibold text-slate-100">
          提交重新激活提案 · 工作区 #{ws.id}
        </h3>
        <p className="mt-1 text-xs text-slate-400">
          工作区名称：{ws.name || '—'} · 当前状态 <StatusPill status={ws.registration_status} />
        </p>
        <p className="mt-2 text-xs text-slate-400">
          提交后需另一位 admin 在{' '}
          <Link to="/ops/review-requests" className="text-chango-300 hover:underline">
            审批收件箱
          </Link>{' '}
          审批，再执行 POST /:id/reactivate-from-archive 完成。
        </p>

        <label className="mt-4 block">
          <span className="text-sm font-medium text-slate-200">理由</span>
          <textarea
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none"
            placeholder="为什么要重新激活这个工作区？（≥8 字符，≤1000 字符）"
            maxLength={REASON_MAX}
          />
          <span className="mt-1 block text-xs text-slate-500">
            {reasonTrim.length} / {REASON_MAX} · 最少 {REASON_MIN} 字符
          </span>
        </label>

        {err && <Banner kind="error">{err}</Banner>}

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-slate-600 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"
            disabled={busy}
          >
            取消
          </button>
          <button
            onClick={submit}
            disabled={!formOk}
            className="rounded-md bg-chango-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-chango-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? '提交中…' : '提交提案'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- main archived list ----------------------------------------------------

function useArchivedWorkspaces({ limit = 100 } = {}) {
  const [state, setState] = useState({
    items: null,
    total: 0,
    loading: true,
    error: null,
  });
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((x) => x + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const res = await api.get(
          `/api/ops/workspaces?status=archived&limit=${limit}&offset=0`
        );
        if (cancelled) return;
        setState({
          items: Array.isArray(res?.items) ? res.items : [],
          total: Number(res?.total || 0),
          loading: false,
          error: null,
        });
      } catch (e) {
        if (cancelled) return;
        setState({
          items: [],
          total: 0,
          loading: false,
          error:
            e instanceof HttpError
              ? `${e.code || 'ERROR'}: ${e.message}`
              : e?.message || 'unknown error',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [limit, reloadKey]);

  return { ...state, reload };
}

// ---- page root -------------------------------------------------------------

export default function OpsArchivedWorkspaces() {
  const { user } = useAuth();
  const { items, total, loading, error, reload } = useArchivedWorkspaces();
  const [dialogWs, setDialogWs] = useState(null);
  const [toast, setToast] = useState(null);

  // Phase 3 RoleGate-B: owner ⊃ admin，两者都允许。owner 在 is_owner（§ST.10 Plan B）
  if (user && user.role !== 'admin' && user.role !== 'owner' && user.is_owner !== true) {
    return (
      <div className="min-h-screen bg-slate-900 p-6 text-slate-100">
        <h1 className="text-lg font-semibold">403 · 仅 admin/owner 可访问</h1>
        <p className="mt-2 text-sm text-slate-400">
          当前角色 <code>{user.role}</code> 无权访问 /ops/archived-workspaces。
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <header className="border-b border-slate-700 bg-slate-800/80">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold">已归档工作区</h1>
            <p className="text-xs text-slate-400">
              §9.6.6 软归档台账 · 两人制重新激活
            </p>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/ops" className="text-slate-400 hover:text-slate-200">
              ← 返回 Ops 控制台
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-6">
        {toast && <Banner kind="ok">{toast}</Banner>}

        <ArchivePreviewTile />

        <section className="rounded-lg border border-slate-700 bg-slate-800/50 p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-100">已归档列表</h2>
            <button
              onClick={reload}
              className="rounded border border-slate-600 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700"
            >
              刷新
            </button>
          </div>

          {error && <Banner kind="error">{error}</Banner>}

          {loading && (
            <p className="mt-3 text-sm text-slate-400">加载中…</p>
          )}

          {!loading && !error && (!items || items.length === 0) && (
            <p className="mt-3 text-sm text-slate-400">
              目前没有已归档的工作区。nightly sweep 到期会填进这里。
            </p>
          )}

          {!loading && items && items.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="py-2 pr-3">#</th>
                    <th className="py-2 pr-3">名称 / 公司</th>
                    <th className="py-2 pr-3">SKU</th>
                    <th className="py-2 pr-3">归档时间</th>
                    <th className="py-2 pr-3">归档前状态</th>
                    <th className="py-2 pr-3 text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {items.map((w) => {
                    const pre =
                      w.frozen_at
                        ? 'frozen'
                        : w.trial_expired_at
                          ? 'trial_expired'
                          : '—';
                    const dAgo = daysAgo(w.archived_at);
                    return (
                      <tr key={w.id} className="text-slate-200">
                        <td className="py-2 pr-3 font-mono text-xs">
                          <Link to={`/ops/workspaces/${w.id}`} className="text-chango-300 hover:underline">
                            {w.id}
                          </Link>
                        </td>
                        <td className="py-2 pr-3">
                          <div className="font-medium">{w.name || '—'}</div>
                          <div className="text-xs text-slate-400">{w.company_name || ''}</div>
                        </td>
                        <td className="py-2 pr-3 text-xs">
                          <code className="rounded bg-slate-900 px-1">{w.sku_code || '—'}</code>
                        </td>
                        <td className="py-2 pr-3 text-xs text-slate-400">
                          {fmtDate(w.archived_at)}
                          {dAgo != null && (
                            <div>{dAgo.toFixed(1)} 天前</div>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs">
                          <span className="text-slate-400">{pre}</span>
                        </td>
                        <td className="py-2 pr-3 text-right">
                          <button
                            onClick={() => setDialogWs(w)}
                            className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700"
                          >
                            申请重新激活
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="mt-2 text-xs text-slate-500">
                共 {total} 条 · 仅显示前 {items.length} 条
              </p>
            </div>
          )}
        </section>
      </main>

      <ReactivateProposeDialog
        open={dialogWs != null}
        ws={dialogWs}
        onClose={() => setDialogWs(null)}
        onSubmitted={(res) => {
          setToast(
            `提案 #${res?.review_request_id} 已入队 · 72 小时内请另一位 admin 在审批收件箱审批。`
          );
          reload();
        }}
      />
    </div>
  );
}
